import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "../../lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

// El modal NUNCA debe salirse de la pantalla. Esta centrado con translate-y-[-50%],
// asi que sin tope de alto lo que sobra se sale por arriba Y por abajo a la vez: se
// van de vista el encabezado (con la X) y el pie (con Guardar/Cancelar), y el modal
// queda sin manera de cerrarse. De ahi `max-h` + `overflow-y-auto`.
//
// `dvh` y no `vh`: en el navegador movil la barra de direcciones aparece y desaparece,
// y con `vh` el pie del modal queda debajo de ella.
//
// `w-[calc(100%-2rem)]` deja un margen lateral en telefono; con `w-full` el modal
// quedaba pegado a los dos bordes y se leia como pantalla completa, no como dialogo.
//
// Si un modal necesita otra cosa, que lo pase por className: cn() usa tailwind-merge,
// asi que la clase del modal le gana a esta. Lo que NO hay que hacer es volver a
// repetir max-h/overflow modal por modal -- esa era la deuda que esto salda.
//
// EN TELEFONO EL MODAL ES PANTALLA COMPLETA (2026-09-05). Antes era la misma tarjeta
// centrada de escritorio, con margen y esquinas redondas, y adentro un formulario de
// nueve campos haciendo scroll en una cajita con el teclado encima; el dueno lo vio mal
// en el celular y la tablet ("hemos tenido muchos problemas"). Un dialogo de esos en
// una pantalla de 360px no es un dialogo, es una pantalla: se le da la pantalla.
//
// Se hace con `max-sm:` y no con el orden movil-primero para que la clase que pasa cada
// modal (`max-w-md`, `max-w-3xl`) siga mandando en escritorio y NO pueda encoger el
// modal en telefono: `max-sm:` es una media query y le gana a la clase sin prefijo.
// Las confirmaciones cortas (`compact`) se quedan como tarjeta tambien en telefono:
// una pregunta de dos lineas a pantalla completa se ve peor.
//
// `grid-cols-1` y no `grid` a secas: sin columna declarada, la columna implicita mide
// por el contenido minimo de lo que tenga adentro, y un texto largo sin cortes (el
// nombre de un archivo, una URL) la ensanchaba mas que el propio dialogo -- todo el
// formulario se salia por la derecha. `grid-cols-1` es minmax(0, 1fr): la columna
// nunca mide mas que el dialogo, y adentro cada quien recorta lo suyo. El dueno lo
// vio al subir el logo de una liga desde el celular (2026-09-05).
const PANTALLA_COMPLETA_EN_TELEFONO =
  "max-sm:inset-0 max-sm:left-0 max-sm:top-0 max-sm:translate-x-0 max-sm:translate-y-0 max-sm:h-dvh max-sm:max-h-none max-sm:w-full max-sm:max-w-none max-sm:rounded-none max-sm:border-0 max-sm:content-start max-sm:data-[state=open]:slide-in-from-left-0 max-sm:data-[state=open]:slide-in-from-top-0 max-sm:data-[state=closed]:slide-out-to-left-0 max-sm:data-[state=closed]:slide-out-to-top-0"

const DialogContent = React.forwardRef(({ className, children, compact = false, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid grid-cols-1 max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 overflow-y-auto rounded-lg border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
        !compact && PANTALLA_COMPLETA_EN_TELEFONO,
        className
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({ className, ...props }) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

// El pie va al final del formulario, sin fijar. Se probo `sticky` y el dueno lo
// rechazo: en un telefono con el teclado abierto una barra fija se come lo poco que
// queda de pantalla. Con el modal a pantalla completa ya no hace falta: se llena de
// arriba abajo y al final esta Guardar, como en cualquier pantalla nativa.
// `gap-2` y no `space-x-2`: en telefono los botones se apilan y el espacio horizontal
// no aplicaba, quedaban pegados.
const DialogFooter = ({ className, ...props }) => (
  <div
    className={cn(
      "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}